import { deviceDelete, devicePage } from "api/device";
import { DeviceDeleteBody, DevicePageRespData } from "api/types/device";
import { usePermissions } from "hooks/usePermissions";
import { t } from "i18next";
import { CONST } from "models/constants";
import { deviceConfiguration } from "models/devices/configuration";
import { InstalledDropdown } from "models/dropdowns/installed";
import { ModelFilterDropdown } from "models/dropdowns/modelFilter";
import { StatusDropdown } from "models/dropdowns/status";
import defineDpTablecolumns, {
  DevicePageTableModel,
} from "models/tables/devicePage";
import { notifySimple } from "providers/toast";
import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { accountSiteList } from "store/appSelectors";
import { setLoading } from "store/appSlice";
import { getDeviceListFilters } from "store/page/pageSelectors";
import { DevicePageFilters, setDevicePageFilters } from "store/page/pageSlice";

import AddDeviceDialog from "widgets/dialogs/addDevice";
import DataQualityDialog from "widgets/dialogs/dataQuality";
import GenericButton from "widgets/controls/buttons/generic";
import Combobox from "widgets/shared/inputs/combobox";
import { SearchInput } from "widgets/shared/inputs/search";
import Table from "widgets/controls/tables/genericTable";
import PlayTokenDialog from "widgets/dialogs/playToken";
import CircleButton from "widgets/controls/buttons/circle-button";
import { MdRefresh } from "react-icons/md";
import { useNotificationDialog } from "providers/notification-dialog-provider";
import SwitchContactorDialog from "widgets/dialogs/switchContactor";

const Devices = () => {
  const { canShow } = usePermissions();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = useNotificationDialog();
  const columns = defineDpTablecolumns(
    (info) => {
      const row = info.row.original;
      handleRowClick(row);
    },
    (info) => {
      const row = info.row.original;
      setSelected(row);
      setIsOpen0(true);
    },
    (info) => {
      const row = info.row.original;
      navigate(`/admin/devices/devtools?snr=${row?.snr}`);
    },
    (info) => {
      const row = info.row.original;
      notify({
        header: t("common.warning"),
        message: `Are you sure you want to delete this device? SNR: ${row?.snr}`,
        okText: t("common.delete"),
        cancelText: t("common.cancel"),
        onOk: () => {
          doDelete(row?.snr);
        },
        onCancel: () => {},
      });
    },
    {
      hideActions: false,
      canSwitchContactor: canShow(CONST.ROLE_OWNER),
    },
    (info) => {
      const row = info.row.original;
      setSelected(row);
      setIsPlayTokenOpen(true);
    },
    (info) => {
      const row = info.row.original;
      setSelected(row);
      setIsSwitchContactorOpen(true);
    }
  );
  const userInfo = useSelector(accountSiteList, shallowEqual);

  // Get initial filters from store once
  const initialFilters = useSelector(getDeviceListFilters);

  // Local state for filters - initialized once from Redux
  const [filters, setFilters] = useState<DevicePageFilters>(
    () => initialFilters
  );

  const [dataSource, setDataSource] = useState<DevicePageRespData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [isOpen0, setIsOpen0] = useState(false);
  const [isPlayTokenOpen, setIsPlayTokenOpen] = useState(false);
  const [isSwitchContactorOpen, setIsSwitchContactorOpen] = useState(false);
  const [selected, setSelected] = useState<DevicePageTableModel>();

  const updatePage = (
    refresh: boolean = false,
    resetFilters: boolean = false
  ) => {
    if (resetFilters) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        paginationState: { pageIndex: 0, pageSize: 10 },
        sortingState: [{ desc: true, id: "name" }],
        totCount: 0,
        pageCount: 0,
      }));
    }
    setSelected(undefined);
    setDialog(false);
    setIsOpen0(false);
    setIsPlayTokenOpen(false);
    setIsSwitchContactorOpen(false);
    if (refresh) {
      fetchData();
    }
  };

  const fetchData = useCallback(async () => {
    const {
      paginationState,
      sortingState,
      classId,
      model,
      status,
      installed,
      search,
    } = filters;
    setIsLoading(true);
    const {
      data,
      status: status2,
      error,
      isLoading,
    } = await devicePage({
      search: search,
      pageIndex: paginationState.pageIndex + 1,
      pageSize: paginationState.pageSize,
      sort: sortingState[0].id,
      desc: sortingState[0].desc,
      classId: classId,
      models: model,
      status: status,
      installed: installed,
    });
    setIsLoading(isLoading);
    if (!error && status2 && status2 === 200) {
      setDataSource(data.devices || []);

      setFilters((prevFilters) => ({
        ...prevFilters,
        paginationState: {
          pageIndex: data.currentPage - 1,
          pageSize: paginationState.pageSize,
        },
        sortingState: sortingState,
        totCount: data.totalCount,
        pageCount: data.totalPages,
      }));

      dispatch(
        setDevicePageFilters({
          paginationState: { pageIndex: 0, pageSize: 10 },
          sortingState: [{ desc: true, id: "name" }],
          totCount: 0,
          pageCount: 0,
          search: search,
          classId: classId,
          model: model,
          status: status,
          installed: installed,
        })
      );
    }
  }, [
    filters.paginationState.pageIndex,
    filters.paginationState.pageSize,
    filters.sortingState,
    filters.classId,
    filters.model,
    filters.status,
    filters.installed,
    filters.search,
  ]);

  const doDelete = async (snr: string) => {
    dispatch(setLoading(true));

    const body: DeviceDeleteBody = {
      snr: snr,
    };
    const { status } = await deviceDelete(body);
    dispatch(setLoading(false));
    if (status === 200) {
      notifySimple(t("notification.success"), "success");
      updatePage(true);
    } else {
      notifySimple(t("notification.error"), "error");
    }
  };

  const handleRowClick = (row: DevicePageTableModel) => {
    if (deviceConfiguration.canDeviceRoute(row.model)) {
      navigate(`/admin/devices/device?snr=${row.snr}`);
    } else {
      notifySimple(t("device.not.supported"), "error");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      {canShow(CONST.ROLE_MANAGER) && (
        <AddDeviceDialog
          isOpen={dialog}
          onClose={() => updatePage(false)}
          onSuccess={() => updatePage(true, true)}
        />
      )}
      <DataQualityDialog
        isOpen={isOpen0}
        device={selected}
        onClose={() => updatePage(false)}
        onSuccess={() => updatePage(true)}
      />
      {isPlayTokenOpen && (
        <PlayTokenDialog
          isOpen={isPlayTokenOpen}
          selected={selected}
          onClose={() => updatePage(false)}
          onSuccess={() => updatePage(false)}
        />
      )}
      {isSwitchContactorOpen && (
        <SwitchContactorDialog
          isOpen={isSwitchContactorOpen}
          selected={selected}
          onClose={() => updatePage(false)}
          onSuccess={() => updatePage(false)}
        />
      )}

      <Table
        filterbar={
          <div className="flex h-full w-full flex-col gap-y-3">
            <div className="flex justify-end gap-2">
              <CircleButton
                onClick={() => {
                  updatePage(true);
                }}
                icon={MdRefresh}
                size="md"
                color="secondary"
                variant="ghost"
                tooltip={"Refresh"}
                tooltipPlacement="bottom"
                disabled={isLoading}
              />
              <GenericButton
                onClick={() => {
                  setDialog(true);
                }}
                type="button"
                text="Add Device"
                size="md"
                permissionRole={CONST.ROLE_OWNER}
              />
            </div>
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  id="device-select-model"
                  placeholder={t("common.select.group")}
                  variant="solid"
                  color="secondary"
                  value={filters.model}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      model: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={ModelFilterDropdown}
                  labelKey="label"
                  valueKey="value"
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  filter
                  id="device-select-site"
                  placeholder={t("common.select.site")}
                  variant="solid"
                  color="secondary"
                  value={filters.classId}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      classId: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={userInfo}
                  labelKey="name"
                  valueKey="id"
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  id="device-select-status"
                  placeholder={t("common.select.status")}
                  variant="solid"
                  color="secondary"
                  value={filters.status}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      status: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={StatusDropdown}
                  labelKey="label"
                  valueKey="value"
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  id="device-select-installed"
                  placeholder={t("common.select.installed")}
                  variant="solid"
                  color="secondary"
                  value={filters.installed}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      installed: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={InstalledDropdown}
                  labelKey="label"
                  valueKey="value"
                  sizes="md"
                  rounded="rounded-xl"
                  permissionRole={CONST.ROLE_MANAGER}
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <SearchInput
                  onChange={(filters) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      search: filters,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  type="text"
                  value={filters.search}
                  sizes="md"
                  rounded="rounded-xl"
                  color="secondary"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        }
        loading={isLoading}
        onRowClick={handleRowClick}
        data={dataSource}
        columns={columns}
        pagination={filters.paginationState}
        paginationOptions={{
          onPaginationChange: (pagination) => {
            const page =
              typeof pagination === "function"
                ? pagination(filters.paginationState)
                : pagination;
            setFilters((prevFilters) => ({
              ...prevFilters,
              paginationState: page,
            }));
          },
          rowCount: filters.totCount,
          pageCount: filters.pageCount,
        }}
        sorting={filters.sortingState}
        onSortingChange={(updaterOrValue) => {
          const newSortingState =
            typeof updaterOrValue === "function"
              ? updaterOrValue(filters.sortingState)
              : updaterOrValue;
          if (newSortingState && newSortingState.length > 0) {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: newSortingState,
            }));
          } else {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: [{ id: "name", desc: false }],
            }));
          }
        }}
      />
    </div>
  );
};

export default Devices;
